import { NetworkRequestList } from "./NetworkRequestList";
import AnalyzeAndParameterizeDrawer from "./ParameterizationDrawer";
import {
  Box,
  Checkbox,
  Chip,
  Drawer,
  FormControlLabel,
  TextField,
  Typography,
  CircularProgress,
  Button,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { useRef, useState, useMemo, useEffect, useCallback } from "react";
import { usePagination } from "@/hooks/usePagination";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { analyzeNetworkInterceptorRequests } from "@/utils/networkInterceptorParameterization";
import { mapNetworkRequestsToInterceptorFormat } from "@/utils/mapNetworkRequestsToInterceptorFormat";
import {
  setParameterizationData,
  closeParameterization,
} from "@/features/parameterization/slice";

interface NetworkTabProps {
  networkRequests: any[];
  selectedTab: string;
  isFromVideoPreview?: boolean;
  currentVideoTime?: number;
  recordingStartTime?: number;
  onSeekToTime?: (timeInSeconds: number) => void;
  reactFlow?: any;
}

const NetworkTab = ({
  networkRequests,
  selectedTab,
  isFromVideoPreview = false,
  currentVideoTime,
  recordingStartTime = 0,
  onSeekToTime,
  reactFlow,
}: NetworkTabProps) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { isOpen: paramIsOpen, model: paramModel, originalRequests: paramOriginalRequests } =
    useAppSelector((state) => state.parameterization);

  const isPublicBugPage = window?.location?.pathname.includes('public/bug');

  // Theme colors based on public/private context
  const themeColors = useMemo(() => {
    if (isPublicBugPage) {
      return {
        containerBg: "#ffffff",
        filterBarBg: "#f9f9f9",
        borderColor: "#e0e0e0",
        textPrimary: "#333333",
        textSecondary: "#666666",
        inputBg: "#ffffff",
        chipBg: "#e0e0e0",
        chipBgActive: theme.palette.primary.main,
        chipColorActive: "#ffffff",
      };
    }
    return {
      containerBg: theme.palette.background.paper,
      filterBarBg: theme.palette.background.default,
      borderColor: theme.palette.divider,
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
      inputBg: theme.palette.background.paper,
      chipBg: theme.palette.action.selected,
      chipBgActive: theme.palette.primary.main,
      chipColorActive: "#ffffff",
    };
  }, [isPublicBugPage, theme]);

  const [filterText, setFilterText] = useState("");
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [expandedRequest, setExpandedRequest] = useState<string | null>(null);

  // Ref for the container that holds the split view
  const networkContainerRef = useRef<HTMLDivElement>(null);

  const [expandedView, setExpandedView] = useState("Headers");

  // --- RESIZING STATE ---
  const [isDragging, setIsDragging] = useState(false);
  // Default split: 50% for list, 50% for details
  const [_networkSplitPosition, setNetworkSplitPosition] = useState(50);


  // 2. Handle Global Mouse Events (Move & Up)
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Use the ref that wraps the split view (passed to NetworkRequestList)
      if (!networkContainerRef.current) return;

      const containerRect = networkContainerRef.current.getBoundingClientRect();

      // Calculate cursor position relative to the container
      const relativeX = e.clientX - containerRect.left;

      // Convert to percentage
      const newPercent = (relativeX / containerRect.width) * 100;

      // Clamp between 20% and 80% to keep panels visible
      setNetworkSplitPosition(Math.max(20, Math.min(80, newPercent)));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      // Reset cursor styles
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    // Attach listeners to document to handle dragging outside the element
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    // Set global cursor while dragging
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isDragging]);

  const handleRequestClick = (id: string) => {
    setExpandedRequest(expandedRequest === id ? null : id);
    setExpandedView("Headers");
  };

  const filteredNetworkRequests = useMemo(() => {
    return networkRequests?.filter((req) => {
      const textMatch =
        !filterText ||
        req.fullUrl?.toLowerCase().includes(filterText.toLowerCase()) ||
        req.name?.toLowerCase().includes(filterText.toLowerCase()) ||
        String(req.status).includes(filterText);

      const typeMatch = selectedFilter === "All" || req.type === selectedFilter;
      const errorMatch = !errorsOnly || req.status >= 400 || req.error === true;

      return textMatch && typeMatch && errorMatch;
    });
  }, [networkRequests, filterText, selectedFilter, errorsOnly]);

  const {
    visibleItems: paginatedNetworkRequests,
    isAtBottom,
    loadMore,
    isLoading,
  } = usePagination({
    data: filteredNetworkRequests,
    initialCount: 20,
    loadCount: 10,
  });

  const effectiveRequests = isFromVideoPreview
    ? filteredNetworkRequests
    : paginatedNetworkRequests;

  // Find the active request index based on current video time
  const activeRequestId = useMemo(() => {
    if (currentVideoTime === undefined || !effectiveRequests?.length) return null;

    const currentTimeSec = currentVideoTime;
    let closestId: string | null = null;
    let closestDiff = Infinity;

    for (let i = 0; i < effectiveRequests.length; i++) {
      const req = effectiveRequests[i];
      // Network logs use startTime (in ms), or timestamp which could be number or ISO string
      let reqTimeMs = req.startTime;
      if (!reqTimeMs && req.timestamp) {
        reqTimeMs = typeof req.timestamp === 'number'
          ? req.timestamp
          : new Date(req.timestamp).getTime();
      }
      if (!reqTimeMs || isNaN(reqTimeMs)) continue;

      // Calculate relative time from recording start (in seconds)
      const reqRelativeTimeSec = (reqTimeMs - recordingStartTime) / 1000;

      // Request should be at or before current time, and closest to it
      const diff = currentTimeSec - reqRelativeTimeSec;

      if (diff >= 0 && diff < closestDiff) {
        closestDiff = diff;
        closestId = req.id;
      }
    }

    return closestId;
  }, [currentVideoTime, recordingStartTime, effectiveRequests]);

  useEffect(() => {
    if (!isFromVideoPreview) return;
    if (selectedTab !== "network") return;
    const el = networkContainerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [effectiveRequests.length, isFromVideoPreview, selectedTab]);

  const handleAnalyzeAndParameterize = useCallback(() => {
    if (!filteredNetworkRequests?.length) return;
    const mapped = mapNetworkRequestsToInterceptorFormat(filteredNetworkRequests);
    const model = analyzeNetworkInterceptorRequests(mapped);
    dispatch(setParameterizationData({ model, originalRequests: mapped }));
  }, [filteredNetworkRequests, dispatch]);

  const handleCloseParameterization = useCallback(() => {
    dispatch(closeParameterization());
  }, [dispatch]);

  return (
    <Box
      sx={{
        display: selectedTab === "network" ? "flex" : "none",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        width: "100%",
        bgcolor: themeColors.containerBg,
      }}
    >
      {/* Filter Bar */}
      <Box
        sx={{
          p: 0.5,
          display: "flex",
          alignItems: "center",
          borderBottom: `1px solid ${themeColors.borderColor}`,
          bgcolor: themeColors.filterBarBg,
        }}
      >
        <Box sx={{ position: "relative", flex: 1 }}>
          <SearchIcon
            sx={{
              position: "absolute",
              left: 8,
              top: 6,
              color: themeColors.textSecondary,
              fontSize: "0.9rem",
            }}
          />
          <TextField
            placeholder="Filter"
            size="small"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                pl: 4,
                height: 28,
                fontSize: "0.75rem",
                color: themeColors.textPrimary,
                bgcolor: themeColors.inputBg,
              },
              width: "100%",
            }}
          />
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={errorsOnly}
              onChange={(e) => setErrorsOnly(e.target.checked)}
              size="small"
            />
          }
          label={
            <Typography variant="caption" sx={{ color: themeColors.textPrimary }}>
              Errors only
            </Typography>
          }
          sx={{ ml: 1 }}
        />
      </Box>

      {/* Filter Types */}
      <Box
        sx={{
          display: "flex",
          p: 0.5,
          borderBottom: `1px solid ${themeColors.borderColor}`,
          overflow: "auto",
          bgcolor: themeColors.filterBarBg,
        }}
      >
        {[
          "All",
          "Fetch/XHR",
          "WS",
          "JS",
          "CSS",
          "Media",
          "Font",
          "Doc",
          "Other",
        ].map((filter) => (
          <Chip
            key={filter}
            label={filter}
            onClick={() => setSelectedFilter(filter)}
            sx={{
              m: 0.25,
              fontSize: "0.7rem",
              height: 24,
              bgcolor: selectedFilter === filter ? themeColors.chipBgActive : themeColors.chipBg,
              color: selectedFilter === filter ? themeColors.chipColorActive : themeColors.textPrimary,
              "&:hover": {
                bgcolor: selectedFilter === filter ? "primary.dark" : "#d5d5d5",
              },
            }}
            size="small"
          />
        ))}
        {selectedFilter === "Fetch/XHR" && filteredNetworkRequests?.length > 0 && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AutoFixHighIcon />}
            onClick={handleAnalyzeAndParameterize}
            sx={{
              ml: "auto",
              fontSize: "0.7rem",
              height: 24,
              textTransform: "none",
              whiteSpace: "nowrap",
            }}
          >
            Analyze & Parameterize
          </Button>
        )}
      </Box>

      {/* Network Request List & Detail Panel Container */}
      <Box
        sx={{
          flex: 1,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <NetworkRequestList
          expandedRequest={expandedRequest}
          expandedView={expandedView}
          setExpandedView={setExpandedView}
          setExpandedRequest={setExpandedRequest}
          filteredNetworkRequests={effectiveRequests}
          filterText={filterText}
          handleRequestClick={handleRequestClick}
          activeRequestId={activeRequestId}
          recordingStartTime={recordingStartTime}
          onSeekToTime={onSeekToTime}
        />

        {/* Load More button */}
        {!isFromVideoPreview && !isAtBottom && effectiveRequests.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: 1,
              borderTop: `1px solid ${themeColors.borderColor}`,
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={loadMore}
              disabled={isLoading}
              sx={{ fontSize: "0.75rem" }}
            >
              {isLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
              Load More
            </Button>
          </Box>
        )}
      </Box>

      <Drawer
        anchor="right"
        open={paramIsOpen}
        onClose={handleCloseParameterization}
        PaperProps={{ sx: { width: "85vw", maxWidth: "100vw" } }}
      >
        <AnalyzeAndParameterizeDrawer
          open={paramIsOpen}
          onClose={handleCloseParameterization}
          model={paramModel}
          originalRequests={paramOriginalRequests}
          historyEntry={null}
          reactFlow={reactFlow}
        />
      </Drawer>
    </Box>
  );
};

export default NetworkTab;
